"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import {
  Bars3Icon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import ServicesDropdown from "./ServicesDropdown";
import LocationsDropdown from "./LocationsDropdown";
import { ShopDropdown, ShopMobileMenu } from "./ShopMenu";
import { getCitiesByPriority } from "@/lib/data/gtaLocations";
import { getTrackedShopUrl } from "@/lib/external-links";
import { getShopCollectionPath, SHOP_COLLECTION_HANDLES } from "@/lib/shopify-catalog";

const MOBILE_SERVICE_LINKS = [
  { name: "All Solutions", href: "/services" },
  { name: "Water Softeners", href: "/services/water-softener" },
  { name: "Drinking Water", href: "/services/drinking-water" },
  { name: "UV Disinfection", href: "/services/uv-system" },
  { name: "Residential Water", href: "/services/residential" },
  { name: "Check Water Hardness", href: "/water-hardness" },
  { name: "Business Solutions", href: "/services/business" },
  { name: "PLC Industrial Programming", href: "/services/plc-industrial-programming" },
];

const MOBILE_LOCATION_LINKS = [
  { name: "All Locations", href: "/locations" },
  ...getCitiesByPriority().slice(0, 8).map((city) => ({
    name: city.displayName,
    href: `/locations/${city.slug}`,
  })),
];

const DESKTOP_LINKS = [
  { name: "Projects", href: "/showcase" },
  { name: "Resources", href: "/blog" },
];

function pathIsActive(pathname, href) {
  return pathname === href || (href !== "/" && pathname?.startsWith(`${href}/`));
}

function MobileAccordion({ id, label, isOpen, onToggle, children }) {
  return (
    <div className="border-b border-water-border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={id}
        className="flex min-h-14 w-full items-center justify-between px-1 py-3 text-left text-lg font-semibold text-water-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue"
      >
        {label}
        <ChevronDownIcon
          className={`h-5 w-5 text-water-muted transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {isOpen ? (
        <div id={id} className="pb-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export default function NavbarComponent() {
  const pathname = usePathname();
  return <NavbarForPath key={pathname} pathname={pathname} />;
}

function NavbarForPath({ pathname }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(Boolean(pathname?.startsWith("/services")));
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(Boolean(pathname?.startsWith("/locations")));
  const menuTriggerRef = useRef(null);
  const menuDialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const desktopCloseTimerRef = useRef(null);
  const servicesTriggerRef = useRef(null);
  const locationsTriggerRef = useRef(null);
  const shopTriggerRef = useRef(null);

  const shopUrl = getTrackedShopUrl(getShopCollectionPath(SHOP_COLLECTION_HANDLES.bestSellers), {
    utm_campaign: "top_navigation",
    utm_content: "shop_button",
  });

  const clearDesktopCloseTimer = () => {
    if (desktopCloseTimerRef.current) {
      window.clearTimeout(desktopCloseTimerRef.current);
      desktopCloseTimerRef.current = null;
    }
  };

  const openDesktopDropdown = (name) => {
    clearDesktopCloseTimer();
    setOpenDropdown(name);
  };

  const closeDesktopDropdown = () => {
    clearDesktopCloseTimer();
    setOpenDropdown(null);
  };

  const scheduleDesktopDropdownClose = () => {
    clearDesktopCloseTimer();
    desktopCloseTimerRef.current = window.setTimeout(() => {
      setOpenDropdown(null);
      desktopCloseTimerRef.current = null;
    }, 140);
  };

  useEffect(() => () => clearDesktopCloseTimer(), []);

  useEffect(() => {
    if (!openDropdown) return undefined;

    const handlePointerDown = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('nav[aria-label="Global"]')) {
        clearDesktopCloseTimer();
        setOpenDropdown(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        const triggerByMenu = {
          services: servicesTriggerRef,
          locations: locationsTriggerRef,
          shop: shopTriggerRef,
        };
        const trigger = triggerByMenu[openDropdown]?.current;
        clearDesktopCloseTimer();
        setOpenDropdown(null);
        window.requestAnimationFrame(() => trigger?.focus());
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openDropdown]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const menuTrigger = menuTriggerRef.current;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileMenuOpen(false);
        return;
      }

      if (event.key !== "Tab" || !menuDialogRef.current) return;

      const focusable = menuDialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      menuTrigger?.focus();
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const desktopLinkClass = (href) =>
    `inline-flex min-h-10 items-center rounded-full px-3.5 text-[15px] font-medium tracking-[-0.01em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2 ${
      pathIsActive(pathname, href)
        ? "bg-water-aqua text-water-blue-dark"
        : "text-water-ink hover:bg-water-canvas hover:text-water-blue-dark"
    }`;

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-water-border/80 bg-white/95 backdrop-blur-xl">
        <nav aria-label="Global" className="mx-auto flex min-h-[4.5rem] w-full max-w-[90rem] items-center gap-4 px-4 sm:px-6 lg:px-6 xl:px-8 2xl:px-12">
          <Link href="/" className="flex flex-none items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2">
            <span className="sr-only">Water Doctor</span>
            <Image
              src="/waterdoctor-logo.png"
              alt="Water Doctor Logo"
              width={1044}
              height={484}
              sizes="132px"
              priority
              style={{ height: "auto" }}
              className="w-[124px] object-contain xl:w-[132px]"
            />
          </Link>

          <div className="ml-auto flex lg:hidden">
            <button
              ref={menuTriggerRef}
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={mobileMenuOpen}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-water-ink transition-colors hover:bg-water-canvas hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>

          <div className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
            <div className="flex items-center gap-0.5">
              <ServicesDropdown
                isOpen={openDropdown === "services"}
                onToggle={() => setOpenDropdown((current) => current === "services" ? null : "services")}
                onOpen={() => openDesktopDropdown("services")}
                onLeave={scheduleDesktopDropdownClose}
                onClose={closeDesktopDropdown}
                triggerRef={servicesTriggerRef}
              />
              <LocationsDropdown
                isOpen={openDropdown === "locations"}
                onToggle={() => setOpenDropdown((current) => current === "locations" ? null : "locations")}
                onOpen={() => openDesktopDropdown("locations")}
                onLeave={scheduleDesktopDropdownClose}
                onClose={closeDesktopDropdown}
                triggerRef={locationsTriggerRef}
              />
              {DESKTOP_LINKS.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={desktopLinkClass(item.href)}
                  onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") closeDesktopDropdown();
                  }}
                  onFocus={closeDesktopDropdown}
                >
                  {item.name}
                </Link>
              ))}
              <ShopDropdown
                isOpen={openDropdown === "shop"}
                onToggle={() => setOpenDropdown((current) => current === "shop" ? null : "shop")}
                onOpen={() => openDesktopDropdown("shop")}
                onLeave={scheduleDesktopDropdownClose}
                onClose={closeDesktopDropdown}
                triggerRef={shopTriggerRef}
              />
            </div>
          </div>

          <div className="hidden flex-none items-center lg:flex">
            <Link
              href="/forms/home-assessment"
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse") closeDesktopDropdown();
              }}
              onFocus={closeDesktopDropdown}
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-water-blue px-5 text-sm font-semibold text-white transition-colors hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
            >
              Request Free Assessment
            </Link>
          </div>
        </nav>
      </header>

      {mobileMenuOpen ? (
        <div
          ref={menuDialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Main menu"
          className="fixed inset-0 z-[70] flex flex-col bg-water-canvas lg:hidden"
        >
          <div className="flex min-h-[4.75rem] flex-none items-center justify-between border-b border-water-border bg-white px-4 sm:px-6">
            <Link href="/" onClick={closeMobileMenu} className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue">
              <Image
                src="/waterdoctor-logo.png"
                alt="Water Doctor Logo"
                width={1044}
                height={484}
                sizes="146px"
                priority
                style={{ height: "auto" }}
                className="w-[132px] object-contain sm:w-[146px]"
              />
            </Link>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={closeMobileMenu}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-water-ink transition-colors hover:bg-water-canvas hover:text-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
            >
              <span className="sr-only">Close main menu</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain">
            <div className="mx-auto w-full max-w-3xl px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-6 sm:px-8">
              <div className="grid gap-3 sm:grid-cols-2">
                <Link
                  href="/forms/home-assessment"
                  onClick={closeMobileMenu}
                  className="inline-flex min-h-12 items-center justify-center rounded-[0.875rem] bg-water-blue px-5 text-sm font-semibold text-white transition-colors hover:bg-water-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                >
                  Request Free Assessment
                </Link>
                <Link
                  href="/landings/help-me-choose"
                  onClick={closeMobileMenu}
                  className="inline-flex min-h-12 items-center justify-center rounded-[0.875rem] border border-water-border bg-white px-5 text-sm font-semibold text-water-ink transition-colors hover:bg-water-aqua focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue focus-visible:ring-offset-2"
                >
                  Help Me Choose
                </Link>
              </div>

              <div className="mt-7 border-t border-water-border">
                <MobileAccordion
                  id="mobile-solutions-links"
                  label="Solutions"
                  isOpen={mobileServicesOpen}
                  onToggle={() => setMobileServicesOpen((current) => !current)}
                >
                  <div className="grid gap-1 sm:grid-cols-2">
                    {MOBILE_SERVICE_LINKS.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={closeMobileMenu}
                        className={`rounded-[0.75rem] px-4 py-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue ${
                          pathIsActive(pathname, item.href)
                            ? "bg-water-aqua font-semibold text-water-blue-dark"
                            : "text-water-muted hover:bg-white hover:text-water-ink"
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </MobileAccordion>

                <MobileAccordion
                  id="mobile-location-links"
                  label="Locations"
                  isOpen={mobileLocationsOpen}
                  onToggle={() => setMobileLocationsOpen((current) => !current)}
                >
                  <div className="grid gap-1 sm:grid-cols-2">
                    {MOBILE_LOCATION_LINKS.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={closeMobileMenu}
                        className={`rounded-[0.75rem] px-4 py-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue ${
                          pathIsActive(pathname, item.href)
                            ? "bg-water-aqua font-semibold text-water-blue-dark"
                            : "text-water-muted hover:bg-white hover:text-water-ink"
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </MobileAccordion>

                {DESKTOP_LINKS.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={closeMobileMenu}
                    className={`flex min-h-14 items-center border-b border-water-border px-1 py-3 text-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-water-blue ${
                      pathIsActive(pathname, item.href) ? "text-water-blue-dark" : "text-water-ink hover:text-water-blue-dark"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}

                <MobileAccordion
                  id="mobile-shop-links-wrapper"
                  label="Shop"
                  isOpen={mobileShopOpen}
                  onToggle={() => setMobileShopOpen((current) => !current)}
                >
                  <ShopMobileMenu onItemClick={closeMobileMenu} />
                </MobileAccordion>
              </div>

              <div className="mt-7 rounded-[1rem] border border-water-border bg-white p-5">
                <p className="text-sm leading-6 text-water-muted">Already know the product category you need?</p>
                <Link
                  href={shopUrl}
                  onClick={closeMobileMenu}
                  className="mt-2 inline-flex text-sm font-semibold text-water-blue-dark underline decoration-water-border decoration-2 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-water-blue"
                >
                  Shop Best Sellers →
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
